import { inject, Injectable, signal, Signal, WritableSignal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { TranslocoService } from '@jsverse/transloco';
import { AvailableLanguage } from './available-language.model';

import { Observable, BehaviorSubject, of, map, catchError, finalize, distinctUntilKeyChanged, skip } from "rxjs";
import { Language } from './language.model';
import { HttpClient } from '@angular/common/http';
import { Location } from '@angular/common';
import { MediaService } from '../content/media/media.service';
import { Tag } from '@frontend/core';
import { RangeInputStep } from '../forms/form-controls/range-input-step.model';
import { LoadingObject, LoadingService } from '../loaders';

@Injectable({
  providedIn: 'root'
})
export class LanguageService {

  languages : Language[];
  _availableLanguages : AvailableLanguage[];
  _characterBasedLanguages : string[];
  public activeLanguageObjectSubject: BehaviorSubject<AvailableLanguage>
  public activeLanguageObject: Observable<AvailableLanguage>
  /**
   * Signal view of the active UI language, for reactive (signal-based) consumers.
   * Kept in lockstep with `activeLanguageObject` — see `setLanguage()`.
   */
  public activeLanguage: Signal<AvailableLanguage>
  /**
   * Emits only on a genuine locale change (never the initial value). Locale-keyed
   * caching services subscribe to this to populate the newly-active language.
   */
  public activeLanguageChanges$: Observable<AvailableLanguage>
  private _languageSkillLevelRangeInputSteps : RangeInputStep[] = [];
  private translocoService = inject(TranslocoService);
  private http = inject(HttpClient);
  private location = inject(Location);
  private mediaService = inject(MediaService);
  private loadingService = inject(LoadingService);
  private _loadingObject = signal<LoadingObject>({});


  constructor(
    // @Inject(DOCUMENT) private document: Document
    ) {

      this._characterBasedLanguages = ['ja','zh','ko',/*'th','vi','hi','bn','gu','kn','ml','mr','pa','ta','te'*/];
      this._availableLanguages = [
        {
          languageKey : 'en',
          name : 'languages.en', // translation key in common translation file
          flag: {hash:'Vy9orn',emoji:'🇬🇧'},
          flag_path: this.mediaService.getFlagUrlFromHash('Vy9orn','c_scale,w_24/',true,'.png'), // 'https://cdn-icons-png.flaticon.com/512/197/197374.png',
          flag_class: 'flag-united-kingdom', // deprecated - this worked with MDB
          change_language: 'Change language'
        },
        {
          languageKey : 'fr',
          name : 'languages.fr', // translation key in common translation file
          flag: {hash:'gy4FJr',emoji:'🇫🇷'},
          flag_path: this.mediaService.getFlagUrlFromHash('gy4FJr','c_scale,w_24/',true,'.png'), // 'https://cdn-icons-png.flaticon.com/512/197/197560.png',
          flag_class: 'flag-france', // deprecated - this worked with MDB
          change_language: 'Changez de langue'
        },
        {
          languageKey : 'de',
          name : 'languages.de', // translation key in common translation file
          flag: {hash:'Oy1Nu6',emoji:'🇩🇪'},
          flag_path: this.mediaService.getFlagUrlFromHash('Oy1Nu6','c_scale,w_24/',true,'.png'), // 'https://cdn-icons-png.flaticon.com/512/197/197571.png',
          flag_class: 'flag-germany', // deprecated - this worked with MDB
          change_language: 'Sprache ändern'
        },
        {
          languageKey : 'es',
          name : 'languages.es', // translation key in common translation file
          flag: {hash:'xj2Q8T',emoji:'🇪🇸'},
          flag_path: this.mediaService.getFlagUrlFromHash('xj2Q8T','c_scale,w_24/',true,'.png'), // 'https://cdn-icons-png.flaticon.com/512/197/197593.png',
          flag_class: 'flag-spain', // deprecated - this worked with MDB
          change_language: 'Cambiar idioma'
        },
        {
          languageKey : 'nl',
          name : 'languages.nl', // translation key in common translation file
          flag: {hash:'TR3L5e',emoji:'🇳🇱'},
          flag_path: this.mediaService.getFlagUrlFromHash('TR3L5e','c_scale,w_24/',true,'.png'), // 'https://cdn-icons-png.flaticon.com/512/197/197565.png',
          flag_class: 'flag-netherlands', // deprecated - this worked with MDB
          change_language: 'Taal wijzigen'
        },
        {
          languageKey : 'sl',
          name : 'languages.sl', // translation key in common translation file
          flag: {hash:'FAF2ei',emoji:'🇸🇮'},
          flag_path: this.mediaService.getFlagUrlFromHash('FAF2ei','c_scale,w_24/',true,'.png'), // 'https://cdn-icons-png.flaticon.com/512/197/197565.png',
          flag_class: 'flag-slovenia', // deprecated - this worked with MDB
          change_language: 'Spremeni jezik'
        },
      ];
      const activeLanguage = this.getUserPreferredLanguage();
      this.activeLanguageObjectSubject = new BehaviorSubject<AvailableLanguage>(activeLanguage);
      this.activeLanguageObject = this.activeLanguageObjectSubject.asObservable();
      // Reactive views of the active language. `requireSync` is safe because the
      // BehaviorSubject always replays a current value synchronously on subscribe.
      this.activeLanguage = toSignal(this.activeLanguageObject, { requireSync: true });
      // Real changes only: drop duplicate same-language emissions (e.g. the
      // constructor's setLanguage() on top of the seed) and the initial value.
      this.activeLanguageChanges$ = this.activeLanguageObject.pipe(
        distinctUntilKeyChanged('languageKey'),
        skip(1),
      );
      for (let i = 0; i <= 6; i++) {
        this._languageSkillLevelRangeInputSteps.push({
          value: i,
          labelTranslationKey: `language.level.${i}.name`,
          labelTranslationScope: 'language',
          descriptionTranslationKey: `language.level.${i}.description`,
          descriptionTranslationScope: 'language',
          descriptionShortTranslationKey: `language.level.${i}.description_short`,
          descriptionShortTranslationScope: 'language',
        });
      }
      this.setLanguage(activeLanguage.languageKey);
      this.getLanguages(true).subscribe();
  }

  get loadingObject(): LoadingObject {
        return this._loadingObject();
    }
    public get loadingObjectSignal() : WritableSignal<LoadingObject> {
        return this._loadingObject;
    }
    isAnythingLoading(): boolean {
        return this.loadingService.isAnythingLoading(this._loadingObject());
    }

    setLoadingStatus(key: string, status: boolean): void {
        this._loadingObject.set(this.loadingService.setLoadingStatus(key, status, this._loadingObject()));
    }

  get languageSkillLevelRangeInputSteps() : RangeInputStep[]{
    return this._languageSkillLevelRangeInputSteps;
  };

  get activeLanguageObjectSynchronously (){
    return this.activeLanguageObjectSubject.value;
  }
  get availableLanguages (){
    return this._availableLanguages;
  }
  get characterBasedLanguages (){
    return this._characterBasedLanguages;
  }
  getUserPreferredLanguage () : AvailableLanguage {
    /* 
    queryParamMap does not work before the Angular application boots, so we cannot use it here (this function is called in app.module in a APP_INITIALIZER)
    const queryParams = this.route.snapshot.queryParamMap;
    // const language = this.route.snapshot.queryParams['language'];
    const languageParam = queryParams.get('language');
    */

    const queryParams = this.location?.path(true).split('?')[1] ?? '';
    const parsedQueryParams = new URLSearchParams(queryParams);
    const languageParam = parsedQueryParams.get('language'); // 'fr' from ?language=fr in the URL

    const userBrowserPreferredLang = navigator.language ? this.availableLanguageKeyForTag(navigator.language) : null;
    const storedLanguage = localStorage.getItem('language');
    const preferredLanguageKey : string = storedLanguage ? storedLanguage : languageParam ? languageParam : userBrowserPreferredLang ?? 'en';
    return this.validatedLanguage(preferredLanguageKey);
  }

  /**
   * The available language key that best matches a BCP 47 tag such as the browser's: the tag
   * itself, then shorter forms of it (`de-CH` → `de`), with its likely script tried before the bare
   * language (`zh-TW` → `zh-Hant` → `zh`). Undefined when nothing matches.
   */
  private availableLanguageKeyForTag (tag : string) : string | undefined {
    const subtags = tag.split('-');
    const candidates = subtags.map((_, i) => subtags.slice(0, subtags.length - i).join('-'));
    try {
      const { language, script } = new Intl.Locale(tag).maximize();
      if (script) {
        candidates.splice(candidates.length - 1, 0, `${language}-${script}`);
      }
    } catch {
      // Not a valid tag: only the plain truncations are tried
    }
    for (const candidate of candidates) {
      const match = this._availableLanguages.find(al => al.languageKey.toLowerCase() === candidate.toLowerCase());
      if (match) {
        return match.languageKey;
      }
    }
    return undefined;
  }
  setLanguage(languageKey : string){
    this.translocoService.setActiveLang(languageKey);
    localStorage.setItem('language',languageKey);
    this.activeLanguageObjectSubject.next(this.validatedLanguage(languageKey));
  }

  validatedLanguage (languageKey : string) : AvailableLanguage{
    if (languageKey){
      let availableLanguage = this._availableLanguages.find(al=>al.languageKey===languageKey);
      if (availableLanguage){
        return availableLanguage;
      }
    }
    return this._availableLanguages.find(al=>al.languageKey===this.getDefaultLang());
  }

  getDefaultLang(){
    return this.translocoService.getDefaultLang()
  }
  getActiveLang(){
    return this.translocoService.getActiveLang()
  }

  getLanguages(freshFromServer:boolean = false): Observable<Language[]> {

    
    if (!freshFromServer && this.languages?.length){
      return of(this.languages);
    };
    this.setLoadingStatus('languages',true);
    return this.http.get< {data: Language[]}>('api/v1/languages')
        .pipe(
          map(response =>{
            if (response?.data?.length ){
              this.languages = response.data;
              return response.data;
            };
            return [];
          }),
          catchError(() => {
            this.setLoadingStatus('languages', false);
            return of([]);
          }),
          finalize(() => {
            this.setLoadingStatus('languages', false);
          })
        );
  };
  convertLanguageToTag (language : Language, preserveId : boolean, cloudinary_base_url: string, flagTransformations: string){
    return new Tag(
      preserveId ? language.id : null,
      language.name,
      language.iso,
      'language',
      null,
      null,
      cloudinary_base_url+flagTransformations+'flags/round/'+language.flag?.hash+'.png'
    )
  }
  convertLanguagesToTags (languages : Language[], preserveId : boolean = false, cloudinary_base_url: string, flagTransformations = 'w_32,c_fill,ar_1:1,r_max/'){
    if(!languages.length){return [];};
    return languages.map(l => this.convertLanguageToTag(l,preserveId,cloudinary_base_url,flagTransformations));
  }
}
